import { useCampaigns } from "api/campaigns";
import { useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { CampaignItem } from "components/Layout/Campaign/Direct/CampaignList/CampaignList";
import { CustomAttributesModal } from "components/Layout/Transaction/SingleTransaction/CustomAttributesModal";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { OverflowChipsCell } from "components/Table/OverflowChipsCell";
import { StatusValue } from "components/Table/StatusValue";
import { calculateCount } from "components/Table/tableHelpers";
import { Tooltip } from "components/Tooltips/Tooltip";
import { handledCampaignTriggers } from "constants/general";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useCallback, useEffect, useState } from "react";
import { selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { GLOBAL_MANAGEMENT_CAMPAIGNS_LIST_ID } from "state/tables/types";
import { CampaignTriggers } from "types/campaigns";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions, ListResponse } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { KeyValuePair } from "utilities/objects";
import { Stores } from "../Stores/Stores";
import { TableProps } from "./Wallets";

export enum Modals {
  CustomAttributes,
}

export const CampaignsTable = ({
  rowsSelected,
  selectRow,
  unselectRow,
  selectAll,
  unselectAll,
  clearSelection,
  selectionDisabled,
}: TableProps) => {
  const { message } = useMessage();
  const tables = useAppSelector(selectTables);
  const { listing, updateListing } = useListing(
    GLOBAL_MANAGEMENT_CAMPAIGNS_LIST_ID,
  );
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[GLOBAL_MANAGEMENT_CAMPAIGNS_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const [isCustomAttributesModal, setIsCustomAttributesModal] = useState(false);
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [customAttributes, setCustomAttributes] = useState<KeyValuePair[]>();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const { useCampaignsList } = useCampaigns();

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      GLOBAL_MANAGEMENT_CAMPAIGNS_LIST_ID,
    );

    setFilters(filters);
  };

  const { data: campaignListData, error } = useCampaignsList({
    filters: [
      { query: "trigger", value: ["time", "leaderboard"], condition: "notIn" },
      {
        query: "type",
        condition: "eq",
        value: "direct",
      },
      ...parseFiltersForBE(getNewFiltersOnly(filters)),
    ],
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "displayOrder",
    dynamicSearch: dynamicSearch,
  }) as { data: ListResponse; error?: any };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && campaignListData) {
      setPersistentFilters(filters, GLOBAL_MANAGEMENT_CAMPAIGNS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, campaignListData, error]);

  const headItems = createHeadItems([
    {
      headerText: message({ id: "name" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "status" }),
      property: "active",
      isSortable: true,
    },
    {
      headerText: message({ id: "campaignType" }),
      property: "campaignType",
      isSortable: false,
    },
    {
      headerText: message({ id: "campaignTrigger" }),
      property: "trigger",
      isSortable: true,
    },
    {
      headerText: message({ id: "activeFrom" }),
      property: "startsAt",
      isSortable: true,
    },
    {
      headerText: message({ id: "activeTo" }),
      property: "endsAt",
      isSortable: true,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      isSortable: true,
    },
    {
      headerText: message({ id: "attributes" }),
      property: "attributes",
      isSortable: false,
    },
  ]);

  const createItems = useCallback(
    (items: CampaignItem[]) => {
      return items.map((item: CampaignItem) => ({
        cells: [
          {
            content: item.description ? (
              <Tooltip text={item.description} placement="bottom-end">
                <span>{item.name}</span>
              </Tooltip>
            ) : (
              item.name
            ),
            property: "name",
          },
          {
            content: <StatusValue status={item.active} />,
            property: "active",
          },
          { content: message({ id: item.type }), property: "campaignType" },
          {
            content: message({
              id: handledCampaignTriggers.find(
                (trigger) => trigger.id === item.trigger,
              )?.label,
            }),
            property: "trigger",
          },
          {
            content: item.campaignActivity?.allTimeActive
              ? message({ id: "isAllTimeActive" })
              : OLDate(item.activity?.startsAt),
            property: "startsAt",
          },
          {
            content: item.campaignActivity?.allTimeActive
              ? message({ id: "isAllTimeActive" })
              : OLDate(item.activity?.endsAt),
            property: "endsAt",
          },
          { content: OLDate(item.createdAt), property: "createdAt" },
          {
            content: item.labels?.length ? (
              <OverflowChipsCell
                labels={item.labels}
                openCustomAttributesModal={() => {
                  setIsCustomAttributesModal(true);
                }}
                setCustomAttributes={setCustomAttributes}
                data-test-id="campaign-list-attributes"
              />
            ) : (
              "-"
            ),
            property: "attributes",
            nonClickable: true,
          },
        ],
        id: item.campaignId,
        metadata: {
          disabled: !item.active,
          showRedemptionCodes:
            item.trigger === CampaignTriggers.CUSTOM_EVENT_UNIQUE_CODE,
        },
        data: item,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignListData],
  );

  const items = campaignListData?.items
    ? createItems(campaignListData.items)
    : [];

  return (
    <>
      <Subheader>
        <FiltersV2
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "startsAt",
              label: message({ id: "activeFrom" }),
              type: "datetime",
            },
            {
              query: "endsAt",
              label: message({ id: "activeTo" }),
              type: "datetime",
            },
            {
              query: "labels",
              label: message({ id: "customAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "direct-campaign-name-filter",
            },
            {
              query: "campaignId",
              label: message({ id: "campaignId" }),
              type: "text",
            },
            {
              query: "trigger",
              label: message({ id: "trigger" }),
              type: "direct-trigger-filter",
            },
          ]}
          data-test-id="campaign-list"
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
        />
        <SubheaderWrapper alignRight>
          <Stores
            tableName={GLOBAL_MANAGEMENT_CAMPAIGNS_LIST_ID}
            clearSelection={clearSelection}
            clearFilters={() => setFilters([])}
          />
          <DynamicSearch property="name" setDynamicSearch={setDynamicSearch} />
        </SubheaderWrapper>
      </Subheader>
      {isCustomAttributesModal && (
        <CustomAttributesModal
          open={isCustomAttributesModal}
          close={() => {
            setIsCustomAttributesModal(false);
            setCustomAttributes(undefined);
          }}
          customAttributes={customAttributes}
        />
      )}
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={GLOBAL_MANAGEMENT_CAMPAIGNS_LIST_ID}
        data-test-id="campaign-list"
        sort={{
          order: listing.order || "asc",
          property: listing.property || "displayOrder",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        columns={campaignListData && headItems}
        rows={campaignListData && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(
            campaignListData?.total.filtered,
            campaignListData?.total.all,
          ),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
        clearFilters={() => setFilters([])}
        selectable={{
          rowsSelected,
          selectRow,
          unselectRow,
          selectAll,
          unselectAll,
          disabled: selectionDisabled,
          clearSelection,
        }}
      />
    </>
  );
};
